import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { AlertCircle, Clock, ClipboardList, ArrowRight, Sparkles } from "lucide-react";

const GROUP_META = {
    now:    { label: "Now",        tone: "rose",    icon: AlertCircle },
    soon:   { label: "Soon",       tone: "amber",   icon: Clock },
    review: { label: "For review", tone: "blue",    icon: ClipboardList },
};

const TONE = {
    rose:   "border-rose-200 bg-rose-50/60 text-rose-900",
    amber:  "border-amber-200 bg-amber-50/60 text-amber-900",
    blue:   "border-blue-200 bg-blue-50/60 text-blue-900",
};

/** VAULT Brief — Home signature widget. Renders NOW / SOON / FOR REVIEW. */
export default function VaultBrief({ firstName = "there" }) {
    const [d, setD] = useState(null);
    const [period, setPeriod] = useState("week");
    useEffect(() => {
        api.get(`/vault-brief?period=${period}`).then((r) => setD(r.data)).catch(() => setD(null));
    }, [period]);
    if (!d) return null;

    const groups = [
        ["now",    d.now || []],
        ["soon",   d.soon || []],
        ["review", d.review || []],
    ].filter(([_, items]) => items.length > 0);

    return (
        <div className="rounded-2xl border border-stone-200 bg-white p-5" data-testid="vault-brief-card">
            <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div>
                    <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-stone-500">Your VAULT Brief</div>
                    <div className="text-[15px] text-stone-900 font-medium mt-1">
                        {d.greeting}, {firstName}.<span className="text-stone-500 font-normal"> Here&rsquo;s what matters today.</span>
                    </div>
                </div>
                <div className="inline-flex gap-1 rounded-full border border-stone-200 p-0.5" data-testid="vault-brief-period">
                    {["today","week","month"].map((p) => (
                        <button key={p} onClick={() => setPeriod(p)}
                                data-testid={`vault-brief-p-${p}`}
                                className={`px-2.5 py-1 rounded-full text-[11px] font-medium ${period===p?"bg-stone-900 text-white":"text-stone-600 hover:bg-stone-100"}`}>
                            {p === "today" ? "Today" : p === "week" ? "This Week" : "This Month"}
                        </button>
                    ))}
                </div>
            </div>
            {groups.length === 0 ? (
                <p className="text-[13px] text-stone-500 mt-3">Nothing urgent — your VAULT is up to date.</p>
            ) : (
                <div className="space-y-3">
                    {groups.map(([g, items]) => {
                        const meta = GROUP_META[g];
                        const Icon = meta.icon;
                        return (
                            <div key={g}>
                                <div className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full border text-[10px] font-bold uppercase tracking-widest ${TONE[meta.tone]}`}>
                                    <Icon size={11} /> {meta.label}
                                </div>
                                <ul className="mt-2 space-y-1.5">
                                    {items.map((it, i) => (
                                        <li key={i} className="flex items-center gap-3 text-[13px]" data-testid={`brief-item-${g}-${i}`}>
                                            <span className="w-1.5 h-1.5 rounded-full bg-stone-400" />
                                            <span className="text-stone-800 flex-1 min-w-0 truncate">
                                                {it.title}
                                                {it.when && <span className="text-stone-500 ml-2 text-[11.5px]">{it.when}</span>}
                                            </span>
                                            <Link to={it.action?.to || "#"} className="inline-flex items-center gap-1 text-[11.5px] font-semibold text-amber-800 hover:text-amber-900">
                                                {it.action?.label || "Open"} <ArrowRight size={10} />
                                            </Link>
                                        </li>
                                    ))}
                                </ul>
                            </div>
                        );
                    })}
                </div>
            )}
            <Link to="/ai" data-testid="vault-brief-ask"
                  className="mt-4 inline-flex items-center gap-1.5 text-[11.5px] text-stone-500 hover:text-amber-800">
                <Sparkles size={11} /> Ask VAULT about this brief →
            </Link>
        </div>
    );
}
